import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardList, ArrowLeft, Save, Send, History } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISAL_ACKNOWLEDGE,
    APPRAISAL_ATTACHMENTS,
    APPRAISAL_SUBMIT,
    APPRAISALS,
} from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import KPIGoalRow from "../../components/appraisal/KPIGoalRow";
import AuditTimeline from "../../components/appraisal/AuditTimeline";
import ReviewPanel from "../../components/appraisal/ReviewPanel";
import FeedbackSummary from "../../components/appraisal/FeedbackSummary";
import GoalAgreementPanel from "../../components/appraisal/GoalAgreementPanel";
import CheckInPanel from "../../components/appraisal/CheckInPanel";
import CompetencyPanel from "../../components/appraisal/CompetencyPanel";
import DevelopmentPlanPanel from "../../components/appraisal/DevelopmentPlanPanel";

import { brandHeadingText, brandMutedText, brandPanelBorder, brandPanelGradient } from "../../components/ui/statusStyles";
import { cn } from "../../utils/tailwindUtils";
import { StatusGuidance } from "../../components/appraisal/AppraisalPieces";
const STATUS = {
    DRAFT: "draft",
    PENDING_GOAL_AGREEMENT: "pending_goal_agreement",
    GOALS_SENT_BACK: "goals_sent_back",
    GOALS_AGREED: "goals_agreed",
    IN_PROGRESS: "in_progress",
    PENDING_EMPLOYEE_SUBMISSION: "pending_employee_submission",
    SENT_BACK_BY_RM: "sent_back_by_rm",
    SENT_BACK_BY_HR: "sent_back_by_hr",
};

const editableStatuses = new Set([
    STATUS.IN_PROGRESS,
    STATUS.PENDING_EMPLOYEE_SUBMISSION,
    STATUS.SENT_BACK_BY_RM,
    STATUS.SENT_BACK_BY_HR,
    STATUS.DRAFT,
]);


/* The reason an appraisal came back. A send-back always carries one -- the
   endpoint refuses without it -- but it lived only in the audit trail, so the
   person who had to act on it never saw it unless they opened History. */
const SentBackNotice = ({ sentBack }) => {
    if (!sentBack?.reason) return null;
    return (
        <div className="rounded-md border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-900/20 p-3 sm:p-4">
            <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                Sent back by {sentBack.by?.name || sentBack.by_role}
            </p>
            <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                &ldquo;{sentBack.reason}&rdquo;
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
                Make the changes below, then submit again.
            </p>
        </div>
    );
};

const MyAppraisalDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update, create, remove } = useApiMethods();

    const [appraisal, setAppraisal] = useState(null);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [confirmAck, setConfirmAck] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [historyKey, setHistoryKey] = useState(0);

    const loadAppraisal = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { id });
            setAppraisal(res?.data || null);
            setGoals(res?.data?.goals || []);
        } catch (_) {
            setAppraisal(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadAppraisal(); }, [loadAppraisal]);

    if (loading) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            </ContainerCard>
        );
    }

    if (!appraisal) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={ClipboardList} title="Appraisal Not Found" />
                <div className="px-3">
                    <Button variant="outline" size="sm" onClick={() => navigate("/appraisal/my-appraisal")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const canEdit = editableStatuses.has(appraisal.status) && !appraisal.is_locked;
    const inGoalPhase = [STATUS.PENDING_GOAL_AGREEMENT, STATUS.GOALS_SENT_BACK, STATUS.GOALS_AGREED]
        .includes(appraisal.status);
    // On a goal-phase cycle, agreeing to the goals *is* the acknowledgement,
    // so the old banner would be a second button for the same decision.
    const needsAck = !inGoalPhase
        && appraisal.status === STATUS.PENDING_EMPLOYEE_SUBMISSION
        && !appraisal.acknowledged_at;
    const allGoalsRated = goals.every((g) => g.self_rating !== null && g.self_rating !== undefined);

    const handleGoalChange = (updated) => {
        setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    };

    const handleAttachmentUpload = async (goalId, file) => {
        try {
            const fd = new FormData();
            fd.append("goal_id", goalId);
            fd.append("file", file);
            const res = await create(APPRAISAL_ATTACHMENTS, fd, true);
            if (res?.status === "success") {
                ToastMsg("success", "File uploaded");
                // Refresh just the goal locally with the new attachment.
                setGoals((prev) => prev.map((g) => (g.id === goalId
                    ? { ...g, attachments: [...(g.attachments || []), res.data] }
                    : g)));
            } else {
                ToastMsg("error", res?.message || "Upload failed");
            }
        } catch (_) {
            ToastMsg("error", "Upload failed");
        }
    };

    const handleAttachmentRemove = async (attachmentId) => {
        try {
            const res = await remove(`${APPRAISAL_ATTACHMENTS}?id=${attachmentId}`);
            if (res?.status === "success") {
                setGoals((prev) => prev.map((g) => ({
                    ...g,
                    attachments: (g.attachments || []).filter((a) => a.id !== attachmentId),
                })));
            } else {
                ToastMsg("error", res?.message || "Failed to remove attachment");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove attachment");
        }
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            const payload = {
                id,
                goals: goals.map((g) => ({
                    id: g.id,
                    employee_progress_note: g.employee_progress_note,
                    self_rating: g.self_rating,
                    self_comment: g.self_comment,
                })),
            };
            const res = await update(APPRAISALS, payload);
            if (res?.status === "success") {
                ToastMsg("success", "Draft saved");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const acknowledge = async () => {
        try {
            const res = await create(APPRAISAL_ACKNOWLEDGE, { id });
            if (res?.status === "success") {
                ToastMsg("success", "Goals acknowledged");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to acknowledge");
            }
        } catch (_) {
            ToastMsg("error", "Failed to acknowledge");
        }
        setConfirmAck(false);
    };

    const submitForReview = async () => {
        if (!allGoalsRated) {
            ToastMsg("error", "Please rate all KPIs before submitting");
            setConfirmSubmit(false);
            return;
        }
        try {
            const payload = {
                id,
                goals: goals.map((g) => ({
                    id: g.id,
                    employee_progress_note: g.employee_progress_note,
                    self_rating: g.self_rating,
                    self_comment: g.self_comment,
                })),
            };
            const res = await create(APPRAISAL_SUBMIT, payload);
            if (res?.status === "success") {
                ToastMsg("success", "Appraisal submitted to Reporting Manager");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to submit");
            }
        } catch (_) {
            ToastMsg("error", "Failed to submit");
        }
        setConfirmSubmit(false);
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={ClipboardList}
                title={appraisal.cycle_name || "Appraisal"}
                subtitle={appraisal.employee?.name ? `${appraisal.employee.designation || ""}` : ""}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/my-appraisal")} className="self-start">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusPill value={appraisal.status} />
                        {appraisal.self_submitted_at && (
                            <span className="text-xs text-muted-foreground">
                                Submitted {dayjs(appraisal.self_submitted_at).format("DD-MM-YYYY")}
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}>
                            <History className="w-4 h-4 mr-1" /> History
                        </Button>
                    </div>
                </div>

                {needsAck && (
                    <div className={cn("rounded-md border p-3 sm:p-4", brandPanelBorder, brandPanelGradient)}>
                        <p className={cn("text-sm font-medium", brandHeadingText)}>Acknowledge your assigned goals to begin</p>
                        <p className={cn("text-xs mt-0.5", brandMutedText)}>
                            Review the KPIs and weightages below. Acknowledgement unlocks the self-evaluation form.
                        </p>
                        <Button size="sm" variant="brand" className="mt-2" onClick={() => setConfirmAck(true)}>
                            Acknowledge Goals
                        </Button>
                    </div>
                )}

                {/* The result, once it is theirs to see. Publishing is the act
                    that discloses it, so this is gated on published_at rather
                    than on the status name. Potential is deliberately absent:
                    it is a planning judgement, never disclosed. */}
                {appraisal.published_at && appraisal.final_score !== null
                    && appraisal.final_score !== undefined && (
                    <div className={cn("rounded-md border p-3 sm:p-4", brandPanelBorder, brandPanelGradient)}>
                        <p className={cn("text-xs uppercase tracking-wide", brandMutedText)}>
                            Your result
                        </p>
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
                            <span className={cn("text-2xl font-semibold", brandHeadingText)}>
                                {Number(appraisal.final_score).toFixed(2)}
                            </span>
                            {appraisal.cycle_rating_scale_max && (
                                <span className={cn("text-sm", brandMutedText)}>
                                    out of {appraisal.cycle_rating_scale_max}
                                </span>
                            )}
                            {appraisal.final_rating && (
                                <span className={cn("text-sm font-medium", brandHeadingText)}>
                                    · {appraisal.final_rating}
                                </span>
                            )}
                        </div>
                        <p className={cn("text-xs mt-1", brandMutedText)}>
                            Published {dayjs(appraisal.published_at).format("DD-MM-YYYY")}
                        </p>
                    </div>
                )}

                <StatusGuidance status={appraisal.status} role="employee" />

                <SentBackNotice sentBack={appraisal.sent_back} />

                <GoalAgreementPanel
                    appraisal={appraisal}
                    onUpdated={(data) => {
                        if (!data) return loadAppraisal();
                        setAppraisal(data);
                        setGoals(data.goals || []);
                        setHistoryKey((k) => k + 1);
                    }}
                />

                {/* 360 is something you act on, so it does not sit behind
                    the History button -- that is for the audit trail. Hidden
                    entirely on a cycle that does not run 360. */}
                {appraisal.cycle_feedback_enabled && (
                    <>
                        <ReviewPanel appraisalId={appraisal.id} mode="nominate"
                            readOnly={!!appraisal.published_at || appraisal.is_locked} />
                        <FeedbackSummary appraisalId={appraisal.id} />
                    </>
                )}

                {showHistory && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <h3 className="text-base font-semibold mb-3">Activity History</h3>
                        <AuditTimeline appraisalId={appraisal.id} refreshKey={historyKey} />
                    </div>
                )}

                <div className="space-y-3">
                    {goals.map((g) => (
                        <KPIGoalRow
                            key={g.id}
                            goal={g}
                            role="employee"
                            readOnly={!canEdit}
                            onChange={handleGoalChange}
                            onAttachmentUpload={handleAttachmentUpload}
                            onAttachmentRemove={handleAttachmentRemove}
                        />
                    ))}
                </div>

                <CheckInPanel
                    appraisalId={appraisal.id}
                    goals={goals}
                    role="employee"
                    locked={appraisal.is_locked}
                />

                <CompetencyPanel
                    appraisal={appraisal}
                    role="employee"
                />

                <DevelopmentPlanPanel
                    appraisal={appraisal}
                    goals={goals}
                    role="employee"
                />

                {canEdit && (
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sticky bottom-0 bg-gradient-to-t from-white dark:from-gray-900 from-50% to-transparent pt-3 -mx-3 px-3 pb-1">
                        <Button variant="outline" onClick={saveDraft} disabled={saving}>
                            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Draft"}
                        </Button>
                        <Button
                            variant="brand"
                            onClick={() => setConfirmSubmit(true)}
                            disabled={!allGoalsRated || saving}
                            title={!allGoalsRated ? "Rate all KPIs first" : undefined}
                        >
                            <Send className="w-4 h-4 mr-1" /> Submit for Review
                        </Button>
                    </div>
                )}
            </div>

            <ConfirmPopupModal
                open={confirmAck}
                onClose={() => setConfirmAck(false)}
                onConfirm={acknowledge}
                title="Acknowledge Goals"
                description="By acknowledging you confirm you've reviewed your assigned KPIs and will work towards them during the cycle period."
                type="info"
                confirmLabel="Acknowledge"
            />

            <ConfirmPopupModal
                open={confirmSubmit}
                onClose={() => setConfirmSubmit(false)}
                onConfirm={submitForReview}
                title="Submit for Review"
                description="Once submitted, you won't be able to edit your self-evaluation unless your Reporting Manager sends it back. Continue?"
                type="warning"
                confirmLabel="Submit"
            />
        </ContainerCard>
    );
};

export default MyAppraisalDetail;
